import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api-client"
import type { PaginatedResponse, Activity } from "@/lib/types"

interface ActivityFilters {
  entity_type?: string
  entity_id?: string
  activity_type?: string
  date_from?: string
  date_to?: string
  is_auto?: boolean
  cursor?: string
  limit?: number
}

function buildParams(filters: ActivityFilters): string {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== "") {
      params.set(key, String(value))
    }
  })
  return params.toString()
}

export function useActivities(filters: ActivityFilters = {}) {
  return useQuery({
    queryKey: ["activities", filters],
    queryFn: () => {
      const qs = buildParams(filters)
      return api.get<PaginatedResponse<Activity>>(`/api/v1/activities${qs ? `?${qs}` : ""}`)
    },
  })
}

export function useEntityTimeline(entityType?: string, entityId?: string) {
  return useQuery({
    queryKey: ["activities", "timeline", entityType, entityId],
    queryFn: () =>
      api.get<PaginatedResponse<Activity>>(
        `/api/v1/activities/timeline?entity_type=${entityType}&entity_id=${entityId}`,
      ),
    enabled: !!entityType && !!entityId,
  })
}

export function useCreateActivity() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post<{ data: Activity }>("/api/v1/activities", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["activities"] })
    },
  })
}

export function useUpdateActivity() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.patch<{ data: Activity }>(`/api/v1/activities/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["activities"] })
    },
  })
}

export function useDeleteActivity() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/activities/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["activities"] })
    },
  })
}
